/**
 * BLU-1436: the stock type of a pick-and-pack (PAP) product code on the Products PAP
 * tab: Own stock, Shared, or Labelled for one client. The setting lives on the code,
 * so one save changes every live PAP SKU starting `<code>` (or equal to it), on any
 * product and any page. Pure: no React.
 */
import type { TFunction } from 'i18next';
import { baseGarmentErrorText, type BaseGarmentErrorInfo } from './base-garment-errors';
import { skuAudienceValue } from './base-garments-audience';
import { papCodeOfSku } from './base-garments-url';

/** The Select value when the code's SKUs on screen disagree. */
export const STOCK_TYPE_MIXED = 'mixed';

export interface PapStockTypeRow {
  sku: string;
  productionType?: string | null;
  isShared?: boolean | null;
  reservedForOrganizationId?: string | null;
  retiredAt?: string | null;
  isTrim?: boolean | null;
  isSupplyAlias?: boolean | null;
}

/**
 * The code a row's stock type is set on, or null when this row takes no part: not PAP,
 * archived, a trim or a supply alias (the save copies to none of them, so they must not
 * make a code read Mixed), or a SKU that does not start with a product code.
 */
export function papStockTypeCodeOf(row: PapStockTypeRow): string | null {
  if (row.retiredAt || row.isTrim || row.isSupplyAlias) return null;
  return papCodeOfSku(row.sku, row.productionType);
}

/**
 * Each code's current stock type (`own`, `all`, `client:<id>` or STOCK_TYPE_MIXED),
 * worked out from the rows on screen in one pass. A code whose rows disagree reads
 * Mixed. Saving makes every SKU of the code agree, so a Mixed code can always be set; a code that agrees on screen and differs on another
 * page is only brought into line by picking a different value (a Select never fires
 * for its own value).
 */
export function papCodeStockTypes(rows: Iterable<PapStockTypeRow>): Map<string, string> {
  const byCode = new Map<string, string>();
  for (const row of rows) {
    const code = papStockTypeCodeOf(row);
    if (code === null) continue;
    const value = skuAudienceValue(row.isShared === true, row.reservedForOrganizationId);
    const seen = byCode.get(code);
    byCode.set(code, seen === undefined || seen === value ? value : STOCK_TYPE_MIXED);
  }
  return byCode;
}

/** The codes a product's rows take part with, A to Z, each once. */
export function papCodesOf(rows: Iterable<PapStockTypeRow>): string[] {
  const codes = new Set<string>();
  for (const row of rows) {
    const code = papStockTypeCodeOf(row);
    if (code !== null) codes.add(code);
  }
  return [...codes].sort();
}

/** True when the refusal says the code is managed in Base garments (it has other SKUs). */
export const isBaseGarmentsRefusal = (info: BaseGarmentErrorInfo | null): boolean =>
  info?.key === 'CODE_IN_USE' && info.detail.key === 'base_garments';

/**
 * The operator sentence for a refused stock-type save. Only "set in Base garments"
 * has its own copy; every other refusal reads as in Base garments, which uses the
 * same Stock type words.
 */
export function stockTypeErrorText(t: TFunction<'inventory'>, info: BaseGarmentErrorInfo, code: string): string {
  if (isBaseGarmentsRefusal(info)) {
    const detailCode = typeof info.detail.code === 'string' && info.detail.code !== '' ? info.detail.code : code;
    return t('products.stockType.inBaseGarments', { code: detailCode });
  }
  return baseGarmentErrorText(t, info, code);
}
